import { Dialog, Typography } from "@norican/react";
import { useMemo } from "react";

import { QrCode } from "@/components/qr-code";
import type { CardProfile } from "@/lib/api";
import { chooseDialogSymbol } from "@/lib/card-qr";

interface QrDialogProps {
  card: CardProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The in-person mode: a large QR code for someone else's phone camera. The
 * full vCard, error correction M (L only for the longest vCards, see
 * `chooseDialogSymbol`) with the standard four-module quiet zone (the symbol's
 * own white border, so the box needs no padding of its own). The box takes
 * the whole dialog body up to min(92vw, 60vh), and the symbol snaps to whole
 * device pixels per module inside it: on a 390 px phone that is 4 css px per
 * module for a normal card (324 px) and 3 for the longest vCards (97 modules
 * incl. quiet zone), 6 px in the desktop dialog.
 */
export function QrDialog({ card, open, onOpenChange }: QrDialogProps) {
  const symbol = useMemo(() => chooseDialogSymbol(card.vcard), [card.vcard]);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md" aria-describedby={undefined}>
          <Dialog.Header>
            <Dialog.Title>{card.display_name}</Dialog.Title>
            <Dialog.CloseButton label="Close" />
          </Dialog.Header>
          {/* On phones the body gives up its side padding so the symbol gets the whole popup width (4 css px per module at 390 px). */}
          <Dialog.Body className="flex flex-col items-center gap-4 max-sm:px-0">
            <QrCode
              value={symbol.value}
              matrix={symbol.matrix}
              quietZone={4}
              label={`QR code with the contact details of ${card.display_name}`}
              className="text-brand-blue-700 flex w-full max-w-[min(92vw,60vh)] justify-center overflow-hidden rounded-lg bg-white shadow-md max-sm:rounded-none max-sm:shadow-none"
            />
            <Typography variant="body-sm" tone="muted" className="text-center max-sm:px-5">
              Point a phone camera at the code to save the contact.
            </Typography>
          </Dialog.Body>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
